import { useAuth } from '@luminovo/auth';
import * as Sentry from '@sentry/react';
import React from 'react';

// Times Auth0's bootstrap phases, to find where the ~2 minute logins customers in China/APAC report stall.
// Replays show nothing fetching for ~1.5 min; production's in-memory token cache forces iframe silent auth (60 s).
// `app_interactive - sdk_ready` is `getAccessTokenSilently`, then what `_authenticated`'s `beforeLoad` waits for
// (the session's round trip and the user's language catalog), then the page's chunk.
// Every milestone is ms since navigation start.

const SLOW_AUTH_THRESHOLD_MS = 8_000;

const reported = new Set<string>();

type MilestoneExtra = Record<string, unknown>;

function connectionInfo(): MilestoneExtra {
    const nav = navigator as Navigator & {
        connection?: { effectiveType?: string; rtt?: number; downlink?: number; saveData?: boolean };
    };
    const c = nav.connection;
    return {
        online: navigator.onLine,
        effectiveType: c?.effectiveType,
        rttMs: c?.rtt,
        downlinkMbps: c?.downlink,
        saveData: c?.saveData,
    };
}

/** Reports each `name` at most once per page load, in ms since navigation start. */
export function reportAuthBootstrapMilestone(name: string, extra: MilestoneExtra = {}): void {
    if (reported.has(name)) {
        return;
    }
    reported.add(name);

    try {
        const elapsedMs = Math.round(performance.now());
        const data = { elapsedMs, ...connectionInfo(), ...extra };

        Sentry.addBreadcrumb({ category: 'auth.bootstrap', message: name, level: 'info', data });
        // Best-effort: attaches to the pageload span while it is still open.
        const span = Sentry.getActiveSpan();
        if (span) {
            Sentry.getRootSpan(span).setAttribute(`auth.${name}.ms`, elapsedMs);
        }

        // Reliable signal: a slow login surfaces as an issue even if the trace is sampled out.
        if (elapsedMs >= SLOW_AUTH_THRESHOLD_MS) {
            Sentry.captureMessage(`Slow auth bootstrap: ${name} after ${(elapsedMs / 1000).toFixed(1)}s`, {
                level: 'warning',
                tags: { 'auth.milestone': name, 'auth.slow': 'true' },
                extra: data,
            });
        }
    } catch {
        // Swallow: telemetry must not affect the login/bootstrap flow.
    }
}

/** Reports `sdk_ready` once the Auth0 SDK finishes its initial `isLoading`: phase 1 of the login stall. */
function AuthBootstrapTelemetryInner(): null {
    // `auth0OrgId` only affects token and login requests, which this probe never makes.
    const { isLoading, isAuthenticated, error } = useAuth({ auth0OrgId: undefined });

    React.useEffect(() => {
        if (!isLoading) {
            reportAuthBootstrapMilestone('sdk_ready', {
                isAuthenticated,
                authError: error?.message,
            });
        }
    }, [isLoading, isAuthenticated, error]);

    return null;
}

/** Mount inside the Auth0 provider; a throw here renders nothing instead of taking down the bootstrap. */
export function AuthBootstrapTelemetry(): React.ReactElement {
    return (
        <Sentry.ErrorBoundary fallback={<></>}>
            <AuthBootstrapTelemetryInner />
        </Sentry.ErrorBoundary>
    );
}
